import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Badge,
  useColorModeValue,
  Icon,
  Skeleton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Flex,
} from '@chakra-ui/react';
import { FaTag, FaEuroSign, FaInfoCircle, FaSearch } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import MerchProductPreview from '../components/Merch/MerchProductPreview';

// CIL Colors
const cilColors = {
  yellow: '#ffcc00',
  teal: '#3eb297',
  sky: '#36a9e1',
  pink: '#e95995',
  plum: '#3b235a',
  red: '#e73341',
};

const MERCH_SKELETON_KEYS = [
  'merch-skeleton-1',
  'merch-skeleton-2',
  'merch-skeleton-3',
  'merch-skeleton-4',
  'merch-skeleton-5',
  'merch-skeleton-6',
  'merch-skeleton-7',
  'merch-skeleton-8',
];

// Helper per normalizzare i dati del prodotto (gestisce sia Strapi v4 che v5)
const normalizeMediaImage = (media) => {
  if (!media) return null;

  if (typeof media === 'string') {
    return { url: media };
  }

  if (media.data) {
    const attrs = media.data.attributes || media.data;
    const path = attrs?.url || attrs?.formats?.small?.url || null;
    if (!path) return null;
    const url = path.startsWith('http')
      ? path
      : `${process.env.REACT_APP_STRAPI_URL || ''}${path}`;
    return { url };
  }

  if (media.url) {
    const url = media.url.startsWith('http')
      ? media.url
      : `${process.env.REACT_APP_STRAPI_URL || ''}${media.url}`;
    return { url };
  }

  return null;
};

const normalizeProduct = (product) => {
  if (!product) return null;

  // Se ha attributes, è Strapi v5
  if (product.attributes) {
    return {
      id: product.documentId || product.id,
      documentId: product.documentId,
      nome: product.attributes.nome,
      descrizione: product.attributes.descrizione,
      descrizioneBreve: product.attributes.descrizioneBreve,
      disponibilita: product.attributes.disponibilita,
      quantita: product.attributes.quantita,
      prezzo: product.attributes.prezzo,
      visibile: product.attributes.visibile,
      prenotabile: product.attributes.prenotabile === true,
      costoSpedizione: Math.max(
        0,
        parseFloat(product.attributes.costoSpedizione) || 0
      ),
      immaginePrincipale: normalizeMediaImage(
        product.attributes.immaginePrincipale
      ),
      immagini:
        product.attributes.immagini?.data
          ?.map((img) => normalizeMediaImage({ data: img }))
          .filter(Boolean) || [],
      tags: product.attributes.tags,
      caratteristiche: product.attributes.caratteristiche,
      sku: product.attributes.sku,
      tipo: product.attributes.tipo,
      nomeBase: product.attributes.nomeBase,
      nomeLinea: product.attributes.nomeLinea,
      taglia: product.attributes.taglia,
      colore: product.attributes.colore,
      lineaKey: product.attributes.lineaKey,
      variantMode: product.attributes.variantMode,
      stockDisponibili:
        product.attributes.stockDisponibili ?? product.attributes.quantita,
    };
  }

  // Strapi v4 o struttura già normalizzata
  return {
    id: product.id || product.documentId,
    nome: product.nome,
    descrizione: product.descrizione,
    descrizioneBreve: product.descrizioneBreve,
    disponibilita: product.disponibilita,
    quantita: product.quantita,
    prezzo: product.prezzo,
    visibile: product.visibile,
    prenotabile: product.prenotabile === true,
    costoSpedizione: Math.max(0, parseFloat(product.costoSpedizione) || 0),
    immaginePrincipale: normalizeMediaImage(product.immaginePrincipale),
    immagini: (product.immagini || [])
      .map((img) => normalizeMediaImage(img))
      .filter(Boolean),
    tags: product.tags,
    caratteristiche: product.caratteristiche,
    sku: product.sku,
    tipo: product.tipo,
    nomeBase: product.nomeBase,
    nomeLinea: product.nomeLinea,
    taglia: product.taglia,
    colore: product.colore,
    lineaKey: product.lineaKey,
    variantMode: product.variantMode,
    stockDisponibili: product.stockDisponibili ?? product.quantita,
  };
};

// Helper per normalizzare un ID (converte in stringa per il confronto)
const normalizeId = (id) => {
  if (id === null || id === undefined) return null;
  return String(id);
};

// Helper per calcolare lo sconto applicabile a un prodotto
const getProductDiscount = (productId, discounts) => {
  if (!discounts || discounts.length === 0 || !productId) {
    return null;
  }

  const normalizedProductId = normalizeId(productId);

  const applicableDiscounts = discounts.filter((discount) => {
    if (discount.type === 'all-products') {
      return true;
    }
    if (
      discount.type === 'products' &&
      discount.productIds &&
      Array.isArray(discount.productIds)
    ) {
      return discount.productIds.some(
        (id) => normalizeId(id) === normalizedProductId
      );
    }
    return false;
  });

  if (applicableDiscounts.length === 0) {
    return null;
  }

  return applicableDiscounts.reduce((best, current) =>
    current.percentageAmount > best.percentageAmount ? current : best
  );
};

// Helper per calcolare il prezzo scontato
const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
  if (!discountPercentage || discountPercentage <= 0) return originalPrice;
  return originalPrice * (1 - discountPercentage / 100);
};

const normalizeCatalogLine = (line) => {
  const immaginiColori = (line.immaginiColori || [])
    .map((item) => ({
      colore: item.colore,
      immagine: normalizeMediaImage(item.immagine),
    }))
    .filter((item) => item.immagine?.url);

  const immagineLista = normalizeMediaImage(line.immagineLista);
  const representative = normalizeProduct(line.representative);

  // Fallback: if API has no per-color images yet, keep single list/main image
  const previewImages =
    immaginiColori.length > 0
      ? immaginiColori.map((item) => item.immagine)
      : [immagineLista, representative?.immaginePrincipale].filter(
          (img) => img?.url
        );

  return {
    ...line,
    representative,
    immagineLista,
    immaginiColori,
    previewImages,
  };
};

const getLineColorBadge = (line) => {
  const { colorCount, variantMode } = line;
  if (!colorCount || variantMode === 'size_only') return null;
  return colorCount === 1 ? '1 colore' : `${colorCount} colori`;
};

const Merch = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [catalogLines, setCatalogLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  const [hoveredLineKey, setHoveredLineKey] = useState(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // ── Load available discounts ──────────────────────────────────────────
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!userData) {
        setAvailableDiscounts([]);
        return;
      }

      try {
        const response = await fetch('/api/user/discounts');
        if (response.ok) {
          const data = await response.json();
          setAvailableDiscounts(data.discounts || []);
        } else {
          setAvailableDiscounts([]);
        }
      } catch (err) {
        console.error('[Merch] Errore nel caricamento degli sconti:', err);
        setAvailableDiscounts([]);
      }
    };

    fetchDiscounts();
  }, [userData]);

  // Fetch prodotti da Strapi
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/products/catalog?populate=*');
        if (!response.ok) {
          throw new Error('Errore nel recupero dei prodotti');
        }
        const data = await response.json();
        const lines = (data.lines || [])
          .map(normalizeCatalogLine)
          .filter((line) => line.representative);

        setCatalogLines(lines);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredLines = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return catalogLines.filter((line) => {
      const product = line.representative;
      const matchesSearch =
        !search ||
        line.nomeLinea?.toLowerCase().includes(search) ||
        product.nome?.toLowerCase().includes(search) ||
        line.descrizioneBreve?.toLowerCase().includes(search) ||
        product.descrizioneBreve?.toLowerCase().includes(search);

      const matchesAvailability =
        !selectedAvailability ||
        (selectedAvailability === 'prenotabile'
          ? line.prenotabile === true
          : line.disponibile === true);

      return matchesSearch && matchesAvailability;
    });
  }, [catalogLines, searchTerm, selectedAvailability]);

  const handleProductClick = (product) => {
    navigate(`/merch/product/${product.id}`);
  };

  const handleLineClick = (line) => {
    handleProductClick(line.representative);
  };

  if (loading) {
    return (
      <Box bg={bgColor} minH="100vh" py={8}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Skeleton height="60px" width="300px" />
            <Skeleton height="40px" width="200px" />
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
              spacing={6}
              width="100%"
            >
              {MERCH_SKELETON_KEYS.map((skeletonKey) => (
                <Box
                  key={skeletonKey}
                  p={6}
                  bg={cardBgColor}
                  borderRadius="lg"
                  border="1px"
                  borderColor={borderColor}
                >
                  <Box width="100%" aspectRatio={1} mb={4}>
                    <Skeleton width="100%" height="100%" />
                  </Box>
                  <Skeleton height="24px" mb={2} />
                  <Skeleton height="16px" mb={2} />
                  <Skeleton height="16px" width="60%" />
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={bgColor} minH="100vh" py={8}>
        <Container maxW="container.xl">
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertTitle>Errore!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="container.xl">
        {/* Header */}
        <VStack spacing={6} mb={8} textAlign="center" width="100%">
          <Heading
            size="2xl"
            color={cilColors.plum}
            textShadow="2px 2px 4px rgba(0,0,0,0.1)"
          >
            Sostieni l&apos;associazione
          </Heading>
          <Text fontSize="lg" color="gray.600" width="100%">
            Acquistando gadget, abbigliamento e materiali CIL contribuisci
            direttamente alle nostre attività: eventi, laboratori creativi,
            progetti per ragazzi e la vita della comunità.
          </Text>
        </VStack>

        {/* Filtri e Ricerca */}
        <Box
          p={6}
          bg={cardBgColor}
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
          mb={8}
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            align={{ base: 'stretch', md: 'center' }}
          >
            <InputGroup flex="1">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cerca prodotti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="lg"
              />
            </InputGroup>

            <Select
              placeholder="Tutti"
              value={selectedAvailability}
              onChange={(e) => setSelectedAvailability(e.target.value)}
              size="lg"
              minW={{ base: '100%', md: '220px' }}
              maxW={{ md: '260px' }}
            >
              <option value="disponibile">Disponibile</option>
              <option value="prenotabile">Prenotabile</option>
            </Select>
          </Flex>
        </Box>

        <Box mb={6}>
          <Text fontSize="lg" fontWeight="semibold">
            {filteredLines.length}{' '}
            {filteredLines.length === 1
              ? 'prodotto trovato'
              : 'prodotti trovati'}
          </Text>
        </Box>

        {filteredLines.length === 0 ? (
          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>
              Nessun prodotto corrisponde ai filtri selezionati.
            </AlertDescription>
          </Alert>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
            {filteredLines.map((line) => {
              const product = line.representative;
              const previewImages = line.previewImages || [];

              const productId = product.documentId || product.id;
              const discount = getProductDiscount(
                productId,
                availableDiscounts
              );
              const hasDiscount = discount && discount.percentageAmount > 0;
              const displayPrice =
                line.minPrice === line.maxPrice ? line.minPrice : line.minPrice;
              const priceLabel =
                line.minPrice !== line.maxPrice
                  ? `da € ${line.minPrice.toFixed(2)}`
                  : null;
              const colorBadge = getLineColorBadge(line);
              const isPreorderLine =
                line.prenotabile === true && line.totalStock <= 0;

              return (
                <Box
                  key={line.key}
                  bg={cardBgColor}
                  borderRadius="lg"
                  border="1px"
                  borderColor={borderColor}
                  overflow="hidden"
                  transition="all 0.3s"
                  cursor="pointer"
                  onClick={() => handleLineClick(line)}
                  onMouseEnter={() => setHoveredLineKey(line.key)}
                  onMouseLeave={() => setHoveredLineKey(null)}
                  _hover={{
                    transform: 'translateY(-4px)',
                    shadow: 'xl',
                    borderColor: cilColors.sky,
                  }}
                >
                  <Box
                    position="relative"
                    width="100%"
                    aspectRatio={1}
                    overflow="hidden"
                    bg="gray.50"
                  >
                    {hasDiscount && (
                      <Badge
                        position="absolute"
                        top={2}
                        left={2}
                        bg={cilColors.yellow}
                        color={cilColors.plum}
                        fontSize="xs"
                        fontWeight="bold"
                        px={2}
                        py={1}
                        borderRadius="md"
                        zIndex={2}
                        boxShadow="0 2px 8px rgba(0, 0, 0, 0.2)"
                      >
                        -{discount.percentageAmount}%
                      </Badge>
                    )}

                    <MerchProductPreview
                      images={previewImages}
                      alt={product.nomeLinea || line.nomeLinea || product.nome}
                      isHovered={hoveredLineKey === line.key}
                    />

                    {isPreorderLine ? (
                      <Box
                        position="absolute"
                        top={2}
                        right={2}
                        width="12px"
                        height="12px"
                        borderRadius="full"
                        bg="orange.500"
                        border="2px solid white"
                        boxShadow="sm"
                      />
                    ) : (
                      product.disponibilita === 'disponibile' && (
                        <Box
                          position="absolute"
                          top={2}
                          right={2}
                          width="12px"
                          height="12px"
                          borderRadius="full"
                          bg={
                            line.totalStock <= 0
                              ? 'red.500'
                              : line.totalStock < 5
                                ? 'orange.500'
                                : 'green.500'
                          }
                          border="2px solid white"
                          boxShadow="sm"
                        />
                      )
                    )}
                  </Box>

                  <Box p={4}>
                    <VStack spacing={3} align="center">
                      <Heading
                        size="md"
                        noOfLines={2}
                        color={cilColors.plum}
                        textAlign="center"
                      >
                        {product.nomeLinea || line.nomeLinea || product.nome}
                      </Heading>

                      {(isPreorderLine ||
                        colorBadge ||
                        (Array.isArray(product.tags) &&
                          product.tags.length > 0)) && (
                        <Flex
                          gap={2}
                          flexWrap={{ base: 'wrap', md: 'nowrap' }}
                          justify="center"
                          align="center"
                          w="100%"
                        >
                          {isPreorderLine && (
                            <Badge
                              colorScheme="orange"
                              variant="subtle"
                              flexShrink={0}
                            >
                              Prenotabile
                            </Badge>
                          )}

                          {colorBadge && (
                            <Badge
                              colorScheme="purple"
                              variant="subtle"
                              flexShrink={0}
                            >
                              {colorBadge}
                            </Badge>
                          )}

                          {Array.isArray(product.tags) &&
                            product.tags.slice(0, 3).map((tag) => (
                              <Badge
                                key={`tag-${product.id}-${tag}`}
                                colorScheme="teal"
                                variant="subtle"
                                size="sm"
                                flexShrink={0}
                              >
                                <Icon as={FaTag} mr={1} />
                                {tag}
                              </Badge>
                            ))}
                        </Flex>
                      )}

                      {displayPrice !== undefined &&
                        (() => {
                          const discountedPrice = discount
                            ? calculateDiscountedPrice(
                                displayPrice,
                                discount.percentageAmount
                              )
                            : displayPrice;

                          return (
                            <Box>
                              <VStack spacing={0} align="center">
                                {hasDiscount && (
                                  <HStack
                                    spacing={1}
                                    align="center"
                                    justify="center"
                                  >
                                    <Icon
                                      as={FaEuroSign}
                                      color="gray.400"
                                      size="xs"
                                    />
                                    <Text
                                      fontSize="xs"
                                      color="gray.400"
                                      textDecoration="line-through"
                                    >
                                      {parseFloat(displayPrice).toFixed(2)}
                                    </Text>
                                  </HStack>
                                )}
                                <HStack
                                  spacing={1}
                                  align="center"
                                  justify="center"
                                >
                                  <Icon
                                    as={FaEuroSign}
                                    color={
                                      hasDiscount ? 'green.600' : cilColors.teal
                                    }
                                    size="sm"
                                  />
                                  <Text
                                    fontSize="md"
                                    fontWeight="semibold"
                                    color={
                                      hasDiscount ? 'green.600' : cilColors.teal
                                    }
                                  >
                                    {priceLabel ||
                                      `€ ${parseFloat(discountedPrice).toFixed(2)}`}
                                  </Text>
                                </HStack>
                              </VStack>
                            </Box>
                          );
                        })()}

                      <Button
                        leftIcon={<FaInfoCircle />}
                        colorScheme="blue"
                        size="sm"
                        width="100%"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product);
                        }}
                        mt={2}
                      >
                        Dettagli
                      </Button>
                    </VStack>
                  </Box>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Container>
    </Box>
  );
};

export default Merch;
